import React, { useContext, useEffect, useState } from "react";
import queryString from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Navigate, Outlet, useLocation } from "react-router";

import Activity from "shared/Activity";
import ProfileDetails from "pages/ContactProfile/ProfileDetails";

import Tabs from "./Tabs";

import usersActions from "actions/users";
import groupsActions from "actions/groups";
import contactActions from "actions/contact";
import onReadContactInformationDispatch from "shared/onReadContactInfoDispatch";
import { Error404, Error500 } from "pages/ErrorPages";
import { Breadcrumb, Dimmer, Loader, Segment } from "semantic-ui-react";
import StickyNotes from "shared/StickyNotes";
import taskTypesActions from "actions/task_types";
import { RecordProfile, STATUS_TYPES, Notification, DateTimeUtils } from "dyl-components";
import { ProfileHeader } from "./ProfileHeader";
import { Link, NavLink } from "react-router-dom";

import "./index.scss";
import { PhoneUtil, StringUtils } from "utils";
import recentsActions from "actions/recents";
import { FAVORITE_TYPE } from "shared/constants/FAVORITE_TYPE";
import { RecordInfo } from "shared/RecordInfo";
import QuoteBuilderProvider from "shared/context/QuoteBuilderProvider";
import SidePanelProvider, { SidePanelContext } from "shared/context/SidePanelProvider";
import RestrictedView from "shared/RestrictedView";
import { USER_PERMISSIONS } from "dyl-components";
import teamsActions from 'actions/teams';

import ArchivedRecordsProvider from "shared/context/ArchivedRecordsProvider";
import ErrorContent from "pages/ErrorPages/subcomponents/ErrorContent";

const ContactProfile = ({
    contact_id,
    contact,
    isInHotlist,
    onHotlistToggle,
    record_source,

    account_stage
}) => {
    const { isSidePanelOpen, onOpenSidePanel, onCloseSidePanel } = useContext(SidePanelContext);
    const { pathname } = useLocation();
    const tab = pathname.split("/")[3];

    const isDetailsSectionOpen = tab === undefined || tab === "";
    const { suffix, first_name, last_name, direct_customer } = contact;

    const created = contact?.received ? DateTimeUtils.formatEpoch(contact.received, DateTimeUtils.WORD_DATETIME_FORMAT) : '';
    const last_modified = contact.received === contact.last_modified ? "" : contact?.last_modified ? DateTimeUtils.formatEpoch(contact.last_modified, DateTimeUtils.WORD_DATETIME_FORMAT) : '';

    const contact_name =
        `${first_name} ${last_name}` + (suffix ? ` ${suffix}` : ``);

    const link = (() => {
        switch (account_stage) {
            case 'contact':
                return '/contacts';
            case 'lead':
                return '/leads?outcome=none';
            case 'opportunity':
                return '/opportunities?outcome=none';
            case 'customer':
                return '/customers';
            default:
                return '/contacts';
        }
    })();

    const dispatch = useDispatch();

    const refresh = () => {
        dispatch(contactActions.readContact(contact_id));
    };

    return (
        <div>
            <ArchivedRecordsProvider>
                <RecordProfile
                    isDetailsSectionOpen={isDetailsSectionOpen}
                    header={
                        <ProfileHeader
                            id={Number(contact_id)}
                            name={contact_name}
                            isInHotlist={isInHotlist}
                            onHotlistToggle={onHotlistToggle}
                            breadcrumbs={
                                <Breadcrumb>
                                    <Breadcrumb.Section
                                        as={NavLink}
                                        to={link}
                                        link
                                    >
                                        {StringUtils.capitalize(account_stage)}
                                    </Breadcrumb.Section>
                                    <Breadcrumb.Divider />
                                    <Breadcrumb.Section active>
                                        {contact_name}
                                    </Breadcrumb.Section>
                                </Breadcrumb>
                            }
                            isSidePanelOpen={isSidePanelOpen}
                            direct_customer={direct_customer}
                        />
                    }
                    details={<ProfileDetails />}
                    tabs={<Tabs />}
                    tabContent={<Outlet />}
                    rightHandContent={
                        <React.Fragment>
                            <StickyNotes record_id={contact_id} />
                            <Segment>
                                <RecordInfo
                                    record_source={record_source}
                                    created={created}
                                    last_modified={last_modified}
                                    assignees={contact.assignees}
                                    record_id={contact_id}
                                    refresh={refresh}
                                    type='contact'
                                    module={account_stage === 'lead' || account_stage === 'opportunity' ? account_stage : null}
                                    record_name={contact_name}
                                />
                            </Segment>
                            <Activity />
                        </React.Fragment>
                    }
                    onToggleSidePanel={(isOpen) => {
                        !isOpen ? onCloseSidePanel() : onOpenSidePanel()
                    }}
                    isSidePanelOpen={isSidePanelOpen}
                />
            </ArchivedRecordsProvider>
        </div>
    );
};

const ContactProfileContainer = () => {
    const params = useParams();
    const { pathname } = useLocation();
    const contact_id = Number(params.contact_id);
    const [isInHotlist, setIsInHotlist] = useState(false);

    const { contact, contactError, contactsInHotlist, current_stage, isReadingContact, record_source, user_id } = useSelector((state) => ({
        current_stage: state.contact.current_stage,
        record_source: state.contact.contact?.record_source || 'User Input',
        isReadingContact:
            state.contact.isReadingContact ||
            state.contact_email.isReadingEmails,
        contact: state.contact.contact,
        contactError: state.contact.contactError,
        contactsInHotlist: state.groups.contactsInHotlist,
        user_id: state.auth.user_id,
    }))
    const dispatch = useDispatch();
    const recents = useSelector(state => state.recents || {});

    useEffect(() => {
        (onReadContactInformationDispatch(dispatch))(contact_id);
        dispatch(usersActions.readUsers());
        dispatch(taskTypesActions.readTaskTypes());
        dispatch(groupsActions.readContactGroups(contact_id));
        dispatch(groupsActions.checkHotlist({ contacts: [contact_id] }));
        dispatch(teamsActions.getUserTeams(user_id));
        if (!recents?.isAddingRecent && pathname?.split("/")?.length === 3) {
            dispatch(recentsActions.addRecent({
                external_id: contact_id,
                page_type: FAVORITE_TYPE.CONTACT
            }))
        }
        // eslint-disable-next-line
    }, [contact_id, dispatch]);

    useEffect(() => {
        setIsInHotlist(contactsInHotlist.includes(Number(contact_id)));
    }, [contactsInHotlist, contact_id])

    useEffect(() => {
        dispatch(contactActions.getRecordsLimits());
    }, [dispatch]);

    // Just making things backwards compatible while we figure the new contact page layout
    if (!contact_id) {
        const { contact_id } = queryString.parse(window.location.search);
        return <Navigate to={`/contact/${contact_id}`} />;
    }

    if (isReadingContact) {
        return (
            <Dimmer active>
                <Loader active />
            </Dimmer>
        );
    }

    if (!isReadingContact && contactError) {
        return <Error500 message={"Something went wrong"} />;
    }

    if (!isReadingContact && !contact) {
        return <Error404 message={"Contact not found"} />;
    }

    if (contact?.archived) {
        return <ErrorContent header={"Record Archived"} error={''} message={<>This record can be found  <Link to={`/settings/archived-records?search=${contact?.first_name + ' ' + contact?.last_name}`}>here</Link></>} />
    }

    const onHotlistToggle = async () => {
        try {
            if (isInHotlist) {
                await dispatch(groupsActions.removeHotlist(contact_id));
                await dispatch(groupsActions.checkHotlist({ contacts: [contact_id] }));
                Notification.alert('Successfully removed contact from hotlist!', STATUS_TYPES.SUCCESS, true);
            } else {
                await dispatch(groupsActions.addToHotlist({ contact_id: [contact_id] }));
                await dispatch(groupsActions.checkHotlist({ contacts: [contact_id] }));
                Notification.alert('Successfully added contact to hotlist!', STATUS_TYPES.SUCCESS, true);
            }
        } catch (error) {
            console.log(error)
            if (isInHotlist) {
                Notification.alert('Problem removing contact from hotlist!', STATUS_TYPES.ERROR, true);
            } else {
                Notification.alert('Problem adding contact to hotlist!', STATUS_TYPES.ERROR, true);
            }
        }
    }

    const account_stage = (() => {
        if (current_stage?.account_stage === 'customer') {
            return 'contact';
        }
        return current_stage?.account_stage || 'contact';
    })();
    return (
        <RestrictedView type="restricted" userPermission={USER_PERMISSIONS.CONTACTS_VIEW} userRestrict assignmentIds={[(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}>
            <ContactProfile
                contact={contact}
                contact_id={contact_id}
                onReadContactInformation={onReadContactInformationDispatch(dispatch)}
                account_stage={account_stage}
                isInHotlist={isInHotlist}
                onHotlistToggle={onHotlistToggle}
                record_source={record_source}
            />
        </RestrictedView>
    );
};

const Container = () => {
    const dispatch = useDispatch();

    const params = useParams();
    const contact_id = Number(params.contact_id);
    const isDialerRunning = useSelector(state => PhoneUtil.isDialerRunning(state.officeView?.dialer));

    return (
        <SidePanelProvider isSidePanelInitiallyOpen={!isDialerRunning}>
            <QuoteBuilderProvider refreshAccountInformation={() => {
                return dispatch(contactActions.readContact(contact_id));
            }}>
                <ContactProfileContainer />
            </QuoteBuilderProvider>
        </SidePanelProvider>
    )
}

export default Container;
